import styled, { useTheme } from 'styled-components';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { KadryOverviewDay } from '@entities/kadry';
import { Panel, PanelHead } from './ui';

/* Динамика за две недели: найдено (линия, правая ось — чисел много),
   предложения и «Подходит» (столбцы, левая ось). */

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(4)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Swatch = styled.span<{ $color: string; $line?: boolean }>`
  display: inline-block;
  width: 12px;
  height: ${({ $line }) => ($line ? '3px' : '10px')};
  margin-right: 6px;
  vertical-align: middle;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;

const Empty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 220px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const dayFmt = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit' });

function label(day: string): string {
  // `YYYY-MM-DD` — календарная дата; разбор без часового пояса браузера.
  const [y, m, d] = day.split('-').map(Number);
  return dayFmt.format(new Date(y, m - 1, d));
}

export function TrendPanel({ days, delay = 0 }: { days: KadryOverviewDay[]; delay?: number }) {
  const theme = useTheme();
  const c = {
    found: theme.colors.text.muted,
    offered: theme.colors.primary,
    interested: theme.colors.status.success,
  };
  const data = days.map((d) => ({ ...d, label: label(d.day) }));
  const empty = days.every((d) => d.found + d.offered + d.interested === 0);
  return (
    <Panel $delay={delay}>
      <PanelHead>
        <h3>Динамика</h3>
        <span>{days.length} дней</span>
      </PanelHead>
      {empty ? (
        <Empty>За две недели событий не было</Empty>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={data} margin={{ top: 8, right: 0, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: theme.colors.text.muted }}
              stroke={theme.colors.border}
              interval="preserveStartEnd"
            />
            <YAxis
              yAxisId="left"
              allowDecimals={false}
              tick={{ fontSize: 11, fill: theme.colors.text.muted }}
              stroke={theme.colors.border}
              width={36}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              allowDecimals={false}
              tick={{ fontSize: 11, fill: theme.colors.text.muted }}
              stroke={theme.colors.border}
              width={36}
            />
            <Tooltip
              cursor={{ fill: theme.colors.surface }}
              contentStyle={{
                background: theme.colors.backgroundElevated,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: 8,
                fontSize: 12,
                color: theme.colors.text.primary,
              }}
            />
            <Bar
              yAxisId="left"
              dataKey="offered"
              name="Предложения"
              fill={c.offered}
              radius={[3, 3, 0, 0]}
              maxBarSize={14}
            />
            <Bar
              yAxisId="left"
              dataKey="interested"
              name="«Подходит»"
              fill={c.interested}
              radius={[3, 3, 0, 0]}
              maxBarSize={14}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="found"
              name="Найдено"
              stroke={c.found}
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
      <Legend>
        <span>
          <Swatch $color={c.offered} />
          Предложения
        </span>
        <span>
          <Swatch $color={c.interested} />
          «Подходит»
        </span>
        <span>
          <Swatch $color={c.found} $line />
          Найдено (правая шкала)
        </span>
      </Legend>
    </Panel>
  );
}
